import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import axios from '../api';
import {
  Avatar, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, IconButton,
  InputLabel, MenuItem as MuiMenuItem, Select, SelectChangeEvent, Snackbar,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography, Alert,
} from '@mui/material';
import {
  Add, Close, Delete as DeleteIcon, Edit as EditIcon, Fastfood as FastfoodIcon,
  Inventory as InventoryIcon, PauseCircleOutlined,
} from '@mui/icons-material';
import LoadingAnimation from '../components/LoadingAnimation';

interface MenuItemRow {
  _id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  images?: string[];
  status: 'active' | 'sold_out' | 'unavailable';
  inventory: { totalQuantity: number; remainingQuantity: number; reservedQuantity: number };
  dietaryInfo?: {
    vegetarian?: boolean;
    vegan?: boolean;
    glutenFree?: boolean;
    dairyFree?: boolean;
    nutFree?: boolean;
    halal?: boolean;
  };
}

const categories = ['Starters', 'Mains', 'Sides', 'Desserts', 'Drinks', 'Breakfast', 'Other'];

const emptyForm = {
  name: '',
  description: '',
  category: 'Mains',
  price: '',
  totalQuantity: '',
  imageUrl: '',
  dietaryInfo: {
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    dairyFree: false,
    nutFree: false,
    halal: false,
  },
};

export default function MerchantMenuItems() {
  const [items, setItems] = useState<MenuItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItemRow | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);

  const [resetTarget, setResetTarget] = useState<MenuItemRow | null>(null);
  const [resetQuantity, setResetQuantity] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<MenuItemRow | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get('/api/merchant/menu-items');
      setItems(response.data.menuItems || []);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const notify = (message: string, severity: 'success' | 'error' | 'warning' = 'success') =>
    setSnackbar({ open: true, message, severity });

  // --- create / edit dialog ---

  const openCreate = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const openEdit = (item: MenuItemRow) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      category: item.category,
      price: String(item.price ?? ''),
      totalQuantity: String(item.inventory?.totalQuantity ?? ''),
      imageUrl: item.images?.[0] || '',
      dietaryInfo: {
        vegetarian: !!item.dietaryInfo?.vegetarian,
        vegan: !!item.dietaryInfo?.vegan,
        glutenFree: !!item.dietaryInfo?.glutenFree,
        dairyFree: !!item.dietaryInfo?.dairyFree,
        nutFree: !!item.dietaryInfo?.nutFree,
        halal: !!item.dietaryInfo?.halal,
      },
    });
    setImagePreview(item.images?.[0] || null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setImagePreview(null);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notify('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notify('Image size must be less than 5MB', 'error');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadData = new FormData();
      uploadData.append('image', file);
      const response = await axios.post('/api/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = response.data.url || response.data.imageUrl;
      setFormData((prev) => ({ ...prev, imageUrl }));
      setImagePreview(imageUrl);
    } catch (err) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setImagePreview(dataUrl);
        setFormData((prev) => ({ ...prev, imageUrl: dataUrl }));
        notify('Using local preview. Image will be saved as base64.', 'warning');
      };
      reader.readAsDataURL(file);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, imageUrl: '' }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.description.trim() || !formData.price || !formData.totalQuantity) {
      notify('Please fill in name, description, price and total quantity', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        images: formData.imageUrl ? [formData.imageUrl] : [],
        price: parseFloat(formData.price),
        inventory: { totalQuantity: parseInt(formData.totalQuantity, 10) },
        dietaryInfo: formData.dietaryInfo,
      };
      if (editingItem) {
        await axios.put(`/api/merchant/menu-items/${editingItem._id}`, payload);
        notify('Menu item updated');
      } else {
        await axios.post('/api/merchant/menu-items', payload);
        notify('Menu item added to your menu');
      }
      closeDialog();
      fetchItems();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not save this menu item', 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- row actions ---

  const toggleAvailability = async (item: MenuItemRow) => {
    try {
      if (item.status === 'unavailable') {
        // Just flip the status back — leave remaining stock untouched
        // (only the dedicated reset-stock action should change quantity).
        await axios.put(`/api/merchant/menu-items/${item._id}`, { status: 'active' });
        notify(`${item.name} is available again`);
      } else {
        await axios.put(`/api/merchant/menu-items/${item._id}/unavailable`);
        notify(`${item.name} marked unavailable`);
      }
      fetchItems();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not update availability', 'error');
    }
  };

  const openReset = (item: MenuItemRow) => {
    setResetTarget(item);
    setResetQuantity(String(item.inventory?.totalQuantity ?? ''));
  };

  const confirmReset = async () => {
    if (!resetTarget) return;
    try {
      await axios.put(`/api/merchant/menu-items/${resetTarget._id}/reset-inventory`, {
        totalQuantity: parseInt(resetQuantity, 10) || resetTarget.inventory.totalQuantity,
      });
      notify(`${resetTarget.name} stock reset`);
      setResetTarget(null);
      fetchItems();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not reset stock', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/merchant/menu-items/${deleteTarget._id}`);
      notify(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      fetchItems();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not delete this item — it may have active orders', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Menu Items</Typography>
          <Typography variant="body2" color="text.secondary">
            The regular items customers can add alongside your surprise boxes at checkout.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Add Item
        </Button>
      </Box>

      <Card>
        {loading ? (
          <Box sx={{ py: 5 }}><LoadingAnimation /></Box>
        ) : error ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography color="error">Failed to load your menu.</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <FastfoodIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>No menu items yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add regular menu items so customers can order them alongside your surprise boxes.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add Item
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Photo</strong></TableCell>
                  <TableCell><strong>Item</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Price (<RialSymbol />)</strong></TableCell>
                  <TableCell><strong>Stock</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>
                      <Avatar
                        src={item.images?.[0]}
                        alt={item.name}
                        variant="rounded"
                        sx={{ width: 40, height: 40 }}
                      >
                        <FastfoodIcon fontSize="small" />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.description?.substring(0, 50)}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip label={item.category} size="small" /></TableCell>
                    <TableCell>{item.price?.toFixed(2)}</TableCell>
                    <TableCell>
                      {item.inventory?.remainingQuantity ?? 0} / {item.inventory?.totalQuantity ?? 0}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status.replace('_', ' ')}
                        size="small"
                        color={item.status === 'active' ? 'success' : item.status === 'sold_out' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(item)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => toggleAvailability(item)}
                        title={item.status === 'unavailable' ? 'Make available' : 'Mark unavailable'}
                      >
                        <PauseCircleOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => openReset(item)} title="Reset stock">
                        <InventoryIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(item)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
          <IconButton onClick={closeDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Item Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description *"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={formData.category}
                  onChange={(e: SelectChangeEvent) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.map((c) => <MuiMenuItem key={c} value={c}>{c}</MuiMenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="Price (OMR) *"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                slotProps={{ htmlInput: { step: 0.05, min: 0 } }}
                fullWidth
              />
            </Box>
            <TextField
              label="Total Quantity *"
              type="number"
              value={formData.totalQuantity}
              onChange={(e) => setFormData({ ...formData, totalQuantity: e.target.value })}
              slotProps={{ htmlInput: { min: 0 } }}
              helperText={editingItem ? 'Changing this adjusts remaining stock by the same amount.' : 'How many are available today.'}
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Photo</Typography>
              {imagePreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{ width: '100%', maxWidth: 320, height: 160, objectFit: 'cover', borderRadius: 2, border: '1px solid #ddd' }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    size="small"
                    sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%', maxWidth: 320, height: 120, border: '2px dashed #ccc', borderRadius: 2,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', '&:hover': { borderColor: 'primary.main' },
                  }}
                  onClick={() => document.getElementById('menu-item-image-upload')?.click()}
                >
                  <Add sx={{ fontSize: 36, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">Click to upload a photo</Typography>
                </Box>
              )}
              <input
                id="menu-item-image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              {uploadingImage && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Uploading...
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Dietary Information</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(['vegetarian', 'vegan', 'glutenFree', 'dairyFree', 'nutFree', 'halal'] as const).map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        size="small"
                        checked={formData.dietaryInfo[key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          dietaryInfo: { ...formData.dietaryInfo, [key]: e.target.checked },
                        })}
                      />
                    }
                    label={key === 'glutenFree' ? 'Gluten-free' : key === 'dairyFree' ? 'Dairy-free' : key === 'nutFree' ? 'Nut-free' : key.charAt(0).toUpperCase() + key.slice(1)}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset stock dialog */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Stock</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Reset "{resetTarget?.name}" to a fresh quantity for today.
          </Typography>
          <TextField
            label="New total quantity"
            type="number"
            value={resetQuantity}
            onChange={(e) => setResetQuantity(e.target.value)}
            slotProps={{ htmlInput: { min: 0 } }}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmReset}>Reset</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Menu Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
